import { TinypassSDK } from "@product/rosetta-sdk";
import { useAtomValue } from "jotai";
import { useCallback } from "react";

import { section } from "shared/data";
import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import { checkArticleIsInSections } from "scmp-app/components/article/article-render/helpers";
import { rosettaAtom } from "scmp-app/lib/rosetta";
import type { articleRenderArticle$data } from "scmp-app/queries/__generated__/articleRenderArticle.graphql";

const hardNewsEntityIds: string[] = [
  section.hongKong.entityId,
  section.china.entityId,
  section.asia.entityId,
  section.thisWeekInAsia.entityId,
  section.business.entityId,
  section.economy.entityId,
  section.tech.entityId,
  section.world.entityId,
  section.comment.entityId,
];
const softNewsEntityIds: string[] = [
  section.lifestyle.entityId,
  section.postMagazine.entityId,
  section.sport.entityId,
];

export const usePaywallPosition = (article_: articleRenderArticle$data) => {
  const { checkTargetUserGeoLocation } = useUserGeolocation();

  const isMobileOrTablet = useResponsive(theme.breakpoints.down("desktop"), false);

  const getPaywallPosition = useCallback(
    (schema: Schema) => {
      // return null and fallback to default paywall position if not in target geo location
      const isTargetUserGeoLocation = checkTargetUserGeoLocation(["US"]);
      if (!isTargetUserGeoLocation) return null;

      // return null and fallback to default paywall position if article is short
      const isVeryShortArticle = schema.filter(node => node.type === "p").length < 10;
      if (isVeryShortArticle) return null;

      const isHardNews = checkArticleIsInSections(article_, hardNewsEntityIds, {
        isCheckArticleFirstSectionOnly: true,
      });
      const isSoftNews = checkArticleIsInSections(article_, softNewsEntityIds, {
        isCheckArticleFirstSectionOnly: true,
      });

      const getNthParagraphIndex = (position: number, schema: Schema) => {
        let count = 0;
        for (const [index, element] of schema.entries()) {
          if (count === position) {
            return index;
          }
          if (element.type === "p") {
            count++;
          }
        }
      };

      const isHigherPaywallAtFirstParagraph = isTargetUserGeoLocation && isHardNews;
      const isLowerPaywall = isTargetUserGeoLocation && isSoftNews;

      if (isHigherPaywallAtFirstParagraph) return getNthParagraphIndex(1, schema);
      if (isLowerPaywall)
        return isMobileOrTablet ? getNthParagraphIndex(7, schema) : getNthParagraphIndex(6, schema);
      return null;
    },
    [article_, checkTargetUserGeoLocation, isMobileOrTablet],
  );

  return {
    getPaywallPosition,
  };
};

export const useUserGeolocation = () => {
  const asyncRosettaState = useAtomValue(rosettaAtom);

  const userGeolocation = ((asyncRosettaState?.result?.hasPianoExperienceExecuted &&
    TinypassSDK.loadedInstance?.customVariables.geoLocation) ??
    asyncRosettaState?.result?.instance?.rulesEngine.getOutcome("guest.userGeolocation")) as string;
  const checkTargetUserGeoLocation = (targetGeoLocation: string[] | undefined) => {
    if (!targetGeoLocation) return false;
    return targetGeoLocation.includes(userGeolocation);
  };

  return {
    checkTargetUserGeoLocation,
  };
};
